import { supabaseClient } from "@/lib/supabase";
import type { WorklistItem } from "../utils/buildWorklistItems";
import { insertPayloadFromItem, type WorklistDismissalRow } from "../utils/worklistDismissals";

const DISMISSAL_SELECT =
    "worklist_dismissal_id, user_id, diary_id, kind, tracker_id, schedule_id, schedule_index, event_id, consent_id, task_label, patient_name, hospital_number, organisation_name, due_at, dismissed_at";

const DISMISSAL_SELECT_LEGACY =
    "worklist_dismissal_id, user_id, diary_id, kind, tracker_id, schedule_id, schedule_index, event_id, task_label, patient_name, hospital_number, organisation_name, due_at, dismissed_at";

function withConsentId(
    rows: Array<Omit<WorklistDismissalRow, "consent_id"> & { consent_id?: string | null }>
): WorklistDismissalRow[] {
    return rows.map((row) => ({ ...row, consent_id: row.consent_id ?? null }));
}

/** Personal worklist archive rows for the signed-in user. */
export async function listWorklistDismissals(): Promise<WorklistDismissalRow[]> {
    const { data, error } = await supabaseClient
        .from("worklist_dismissals")
        .select(DISMISSAL_SELECT)
        .order("dismissed_at", { ascending: false });

    if (!error) {
        return withConsentId((data ?? []) as WorklistDismissalRow[]);
    }

    if (error.message.toLowerCase().includes("consent_id")) {
        const fallback = await supabaseClient
            .from("worklist_dismissals")
            .select(DISMISSAL_SELECT_LEGACY)
            .order("dismissed_at", { ascending: false });
        if (fallback.error) {
            console.error("🚨 [listWorklistDismissals]", fallback.error);
            throw fallback.error;
        }
        return withConsentId((fallback.data ?? []) as WorklistDismissalRow[]);
    }

    console.error("🚨 [listWorklistDismissals]", error);
    throw error;
}

export async function archiveWorklistItem(item: WorklistItem, userId: string): Promise<WorklistDismissalRow> {
    if (item.kind === "form" && (item.scheduleId == null || item.scheduleIndex == null || !item.trackerId)) {
        throw new Error("Cannot archive a form task without a schedule slot");
    }
    if (item.kind === "event_details" && !item.detailsTarget?.eventId) {
        throw new Error("Cannot archive procedure details without an event");
    }
    if (item.kind === "consent" && !item.consentId) {
        throw new Error("Cannot archive a consent without a consent id");
    }

    const payload = insertPayloadFromItem(item, userId);
    const { data, error } = await supabaseClient
        .from("worklist_dismissals")
        .insert(payload as never)
        .select(DISMISSAL_SELECT)
        .single();

    if (error) {
        if (error.code === "23505") {
            const existing = await findExistingDismissal(payload);
            if (existing) return existing;
        }
        console.error("🚨 [archiveWorklistItem]", error);
        throw error;
    }

    return data as WorklistDismissalRow;
}

async function findExistingDismissal(
    payload: ReturnType<typeof insertPayloadFromItem>
): Promise<WorklistDismissalRow | null> {
    let query = supabaseClient.from("worklist_dismissals").select(DISMISSAL_SELECT).eq("user_id", payload.user_id);

    if (payload.kind === "form") {
        query = query
            .eq("kind", "form")
            .eq("tracker_id", payload.tracker_id!)
            .eq("schedule_id", payload.schedule_id!)
            .eq("schedule_index", payload.schedule_index!);
    } else if (payload.kind === "consent") {
        query = query.eq("kind", "consent").eq("consent_id", payload.consent_id!);
    } else {
        query = query.eq("kind", "event_details").eq("event_id", payload.event_id!);
    }

    const { data, error } = await query.maybeSingle();
    if (error) {
        console.error("🚨 [findExistingDismissal]", error);
        throw error;
    }
    return (data as WorklistDismissalRow | null) ?? null;
}

export async function restoreWorklistDismissal(dismissalId: string): Promise<void> {
    const { error } = await supabaseClient
        .from("worklist_dismissals")
        .delete()
        .eq("worklist_dismissal_id", dismissalId);

    if (error) {
        console.error("🚨 [restoreWorklistDismissal]", error);
        throw error;
    }
}
